import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchPosts } from "../api";
import Navbar from "./navbar";
import Footer from "./sections/footer";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Newsletter from "../components/Newsletter";

export default function BlogPage() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const getPostTitle = (post) =>
    language === "fr" && post.titleFr ? post.titleFr : post.title;
  const getPostContent = (post) => {
    const content =
      language === "fr" && post.contentFr ? post.contentFr : post.content;
    return content.replace(/<[^>]*>/g, "").slice(0, 150) + "...";
  };

  const loadPosts = async () => {
    try {
      const data = await fetchPosts();
      setPosts(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#131315] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3aa1ff]"></div>
      </div>
    );

  return (
    <>
      <SEO
        title={
          language === "fr"
            ? "Blog | Articles & Insights"
            : "Blog | Articles & Insights"
        }
        description={
          language === "fr"
            ? "Explorez mes réflexions sur le design, la technologie et la créativité."
            : "Exploring the intersection of design, technology, and creativity."
        }
      />
      <Navbar />
      <div className="min-h-screen bg-white pt-10 pb-20 px-5 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="zoom-in">
            <h1 className="font-Poppins font-medium text-slate-400 uppercase tracking-widest text-sm mb-2">
              Insights & Articles
            </h1>
            <h2 className="text-3xl lg:text-2xl font-Poppins font-bold text-[#131315]">
              My <span className="text-[#3aa1ff]">Blog</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto font-Inter mt-4">
              Exploring the intersection of design, technology, and creativity.
              Here are my thoughts on building better digital experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <Link
                to={`/blog/${post.slug}`}
                key={post.id}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="group bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
              >
                <div className="relative aspect-video overflow-hidden bg-gray-50">
                  {post.coverImage ? (
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-12 h-12"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z"
                        />
                      </svg>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#3aa1ff] text-white px-3 py-1 rounded-full text-[10px] font-bold font-Poppins uppercase tracking-tighter">
                      New Article
                    </span>
                  </div>
                </div>

                <div className="p-8 flex flex-col flex-1">
                  <span className="text-[#3aa1ff]/60 text-xs font-Inter font-bold mb-3 uppercase tracking-widest">
                    {new Date(post.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                  <h2 className="text-xl font-Poppins font-bold text-[#131315] mb-4 group-hover:text-[#3aa1ff] transition-colors line-clamp-2">
                    {getPostTitle(post)}
                  </h2>
                  <p className="text-gray-500 text-sm font-Inter line-clamp-3 mb-6 flex-1 leading-relaxed">
                    {getPostContent(post)}
                  </p>
                  <div className="flex items-center text-[#3aa1ff] font-Poppins font-bold text-sm">
                    Read Article
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {posts.length === 0 && (
            <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-200 border-dashed">
              <p className="text-gray-400 italic">
                Writing amazing things... check back soon!
              </p>
            </div>
          )}
        </div>
      </div>
      <Newsletter />
      <Footer />
    </>
  );
}
